"use client";

/**
 * connected-apps-section.tsx — the Composio half of /settings/integrations.
 *
 * Kept as its own component because it is a genuinely different system from the
 * native Drive/Dropbox rows above it: Composio owns the OAuth, the tokens and
 * the refresh, and scopes connections per USER rather than per account. Merging
 * the two into one list would imply a shared model that does not exist.
 *
 * Spec: docs/superpowers/specs/2026-09-18-integrations-page-unification-design.md
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, ExternalLink, Plug, RefreshCcw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

interface AppRow {
  slug: string;
  connected: boolean;
  name?: string | null;
  logo?: string | null;
  description?: string | null;
  tools_count?: number | null;
  in_catalog?: boolean | null;
}

/**
 * Display names only. The LIST comes from COMPOSIO_TOOLKITS on the hub — a
 * toolkit added on Render must appear here without a UI redeploy, so an unknown
 * slug renders as a title-cased slug rather than being filtered out.
 */
const APP_NAMES: Record<string, string> = {
  gmail: "Gmail",
  googlecalendar: "Google Calendar",
  googledrive: "Google Drive",
  googlesheets: "Google Sheets",
  googledocs: "Google Docs",
  slack: "Slack",
  stripe: "Stripe",
  hubspot: "HubSpot",
  notion: "Notion",
  linear: "Linear",
  github: "GitHub",
  airtable: "Airtable",
  calendly: "Calendly",
  quickbooks: "QuickBooks",
  shopify: "Shopify",
  twilio: "Twilio",
  zoom: "Zoom",
  outlook: "Outlook",
  dropbox: "Dropbox",
  asana: "Asana",
  trello: "Trello",
  salesforce: "Salesforce",
  mailchimp: "Mailchimp",
  docusign: "DocuSign",
  xero: "Xero",
  pipedrive: "Pipedrive",
  square: "Square",
  lightspeed: "Lightspeed",
  zoho_mail: "Zoho Mail",
  zohomail: "Zoho Mail",
  fastmail: "Fastmail",
  icloud_mail: "iCloud Mail",
  icloudmail: "iCloud Mail",
  sendgrid: "SendGrid",
  mailgun: "Mailgun",
  postmark: "Postmark",
  aws_ses: "Amazon SES",
  awsses: "Amazon SES",
  resend: "Resend",
};

/**
 * Unknown slugs still render — a toolkit added on Render must appear without a
 * UI redeploy. Underscores become spaces and every word is capitalised, so a
 * new slug reads as "Zoho Mail" rather than "Zoho_mail".
 */
/** Composio's own name wins when we have it; the map is the offline fallback. */
function displayName(app: AppRow): string {
  return app.name?.trim() || appName(app.slug);
}

function appName(slug: string): string {
  const known = APP_NAMES[slug.toLowerCase()];
  if (known) return known;
  return slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Logos come from Composio and are remote URLs, so any of them can fail. A
 * broken-image glyph is its own small lying control — it says "there is a
 * picture here" when there is not. On error we fall back to a lettermark, which
 * is always a real, intended thing to look at.
 */
function AppLogo({ src, name }: { src?: string | null; name: string }) {
  const [broken, setBroken] = useState(false);
  const letter = name.trim().charAt(0).toUpperCase() || "?";

  if (!src || broken) {
    return (
      <div
        aria-hidden
        className="size-8 rounded-lg bg-[var(--surface-high)] flex items-center justify-center shrink-0 text-xs font-bold text-[var(--on-surface-variant)]"
      >
        {letter}
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt=""
      aria-hidden
      width={32}
      height={32}
      onError={() => setBroken(true)}
      className="size-8 rounded-lg object-contain bg-[var(--surface-high)] shrink-0"
    />
  );
}

/** What we could not establish, stated as such — never rendered as "none". */
type LoadState =
  | { kind: "loading" }
  | { kind: "disabled"; reason: string }
  | { kind: "unreachable"; reason: string }
  | { kind: "ready"; apps: AppRow[] };

export function ConnectedAppsSection({
  onToast,
  layout = "list",
  featured,
  collapseOthers = false,
}: {
  /**
   * Slugs to list first with a "Recommended" tag (onboarding highlights the
   * mail, calendar and CRM apps an operator needs). Only reorders what the hub
   * returned — a featured slug the hub does not offer simply is not shown.
   */
  featured?: readonly string[];
  /**
   * Hide non-featured apps behind a "Show all" button. Ignored when none of the
   * featured apps are on offer, so the list can never collapse to nothing.
   */
  collapseOthers?: boolean;
  /**
   * "list" — full-width rows (Settings). "grid" — rounded square tiles, three
   * across (Marketplace, where browsing is the point rather than managing).
   * One component, two presentations: a second component would drift.
   */
  layout?: "list" | "grid";
  /**
   * Optional. The settings page has a toast host and passes one; the
   * marketplace tab has none. Rather than give the marketplace a toast system
   * it does not otherwise need — or ship a second copy of this component — the
   * section falls back to its own inline notice. Failures never route here
   * anyway; they live on the card.
   */
  onToast?: (kind: "success" | "error", msg: string) => void;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<Set<string>>(new Set());
  /**
   * Why per-card and not a toast: a toast is single-slot and auto-dismisses in
   * 4s. Clicking Connect down a list of 23 apps means later failures silently
   * overwrite earlier ones, and a card whose connect FAILED then renders as a
   * plain "Not connected" — indistinguishable from one never tried. That is a
   * lying control, and it is how this surface shipped on 2026-09-18.
   */
  const [failures, setFailures] = useState<Record<string, string>>({});
  const [showAll, setShowAll] = useState(false);
  /** Authorisation URLs whose tab the browser blocked — rendered as a plain link. */
  const [blockedUrls, setBlockedUrls] = useState<Record<string, string>>({});
  const clearBlockedUrl = useCallback((slug: string) => {
    setBlockedUrls((prev) => {
      if (!(slug in prev)) return prev;
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }, []);
  // A stable key, so a caller passing an inline array does not refetch forever.
  const featuredKey = (featured ?? []).map((s) => s.toLowerCase()).join(",");

  const say = useCallback(
    (kind: "success" | "error", msg: string) => {
      if (onToast) onToast(kind, msg);
      else setNotice(msg);
    },
    [onToast],
  );

  const clearFailure = useCallback((slug: string) => {
    setFailures((prev) => {
      if (!(slug in prev)) return prev;
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const resp = await api.integrations.apps.list();
      if (!resp.enabled) {
        setState({
          kind: "disabled",
          reason: resp.reason || "Connected apps are not enabled on this workspace.",
        });
        return;
      }
      setState({ kind: "ready", apps: featureFirst(resp.available ?? [], featuredKey) });
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      // A failure to REACH the provider is not an empty list. Saying "nothing
      // connected" here would be asserting something we could not establish —
      // the same defect that made Composio look broken for six weeks.
      setState({
        kind: "unreachable",
        reason:
          e.code === "PROVIDER_UNREACHABLE" || e.code === "UPSTREAM_UNREACHABLE"
            ? "Could not reach the connection provider, so this list is unknown right now. Your connections are unchanged."
            : e.message || "Could not load connected apps.",
      });
    }
  }, [featuredKey]);

  useEffect(() => {
    void load();
  }, [load]);

  // Returning from the OAuth tab should settle the card without a manual reload.
  useEffect(() => {
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const handleConnect = useCallback(
    async (slug: string) => {
      setBusy(slug);
      clearFailure(slug);
      clearBlockedUrl(slug);
      // Open the tab NOW, synchronously inside the click. Opening it after the
      // await below is outside the user gesture, and Safari/iOS blocks that —
      // with "noopener" the block cannot even be detected, so Connect silently
      // did nothing on phones. We open a blank tab, drop its opener by hand,
      // and point it at the authorisation URL once it arrives.
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      try {
        const resp = await api.integrations.apps.connect(slug);
        if (resp.already_connected) {
          tab?.close();
          say("success", `${appName(slug)} is already connected.`);
          await load();
          return;
        }
        if (!resp.url) {
          tab?.close();
          setFailures((prev) => ({
            ...prev,
            [slug]: `No authorisation link came back. ${appName(slug)} may not be set up on this workspace yet.`,
          }));
          return;
        }
        if (tab && !tab.closed) {
          tab.location.href = resp.url;
        } else {
          // Blocked (or closed by the user already): hand them the link to tap.
          const url = resp.url;
          setBlockedUrls((prev) => ({ ...prev, [slug]: url }));
        }
        // Deliberately NOT marked connected — the user has not approved
        // anything yet. The focus listener above resolves it when they return.
        setConnecting((prev) => new Set(prev).add(slug));
      } catch (err: unknown) {
        tab?.close();
        const msg = err instanceof Error ? err.message : `Could not start the ${appName(slug)} connection.`;
        setFailures((prev) => ({ ...prev, [slug]: msg }));
      } finally {
        setBusy(null);
      }
    },
    [clearBlockedUrl, clearFailure, load, say],
  );

  const handleDisconnect = useCallback(
    async (slug: string) => {
      const ok = window.confirm(
        `Disconnect ${appName(slug)}? Your agents' ${appName(slug)} tools stop working immediately. You can reconnect at any time.`,
      );
      if (!ok) return;
      setBusy(slug);
      try {
        const resp = await api.integrations.apps.disconnect(slug);
        say(
          "success",
          resp.removed === 0
            ? `${appName(slug)} was not connected — nothing to disconnect.`
            : `Disconnected ${appName(slug)}.`,
        );
        setConnecting((prev) => {
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
        await load();
      } catch (err: unknown) {
        // Disconnect failures are per-card too — same reason as connect.
        const msg = err instanceof Error ? err.message : `Could not disconnect ${appName(slug)}.`;
        setFailures((prev) => ({ ...prev, [slug]: msg }));
      } finally {
        setBusy(null);
      }
    },
    [load, say],
  );

  const featuredSet = new Set(featuredKey ? featuredKey.split(",") : []);
  const isFeatured = (slug: string) => featuredSet.has(slug.toLowerCase());
  const hiddenCount =
    state.kind === "ready" ? state.apps.filter((a) => !isFeatured(a.slug)).length : 0;
  const anyFeaturedOffered =
    state.kind === "ready" && state.apps.some((a) => isFeatured(a.slug));
  const collapsing = collapseOthers && !showAll && anyFeaturedOffered && hiddenCount > 0;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--on-surface)]">
            <Plug className="size-4" /> Connected apps
          </h2>
          <p className="text-xs text-[var(--on-surface-variant)]">
            Your own accounts — connecting one lets your agents act in it on your behalf. These are
            personal to you, not shared with the rest of the workspace.
          </p>
        </div>
        {state.kind === "ready" && (
          <button
            type="button"
            onClick={() => void load()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)] transition-colors shrink-0"
          >
            <RefreshCcw className="size-3.5" /> Refresh
          </button>
        )}
      </div>

      {notice && !onToast && (
        <p className="rounded-lg bg-[var(--surface-high)] px-4 py-2.5 text-xs text-[var(--on-surface-variant)]">
          {notice}
        </p>
      )}

      {state.kind === "loading" && (
        <p className="text-xs text-[var(--on-surface-variant)] italic">Loading…</p>
      )}

      {/* A dimmed control with no reason is a lying control. Say why. */}
      {state.kind === "disabled" && (
        <p className="rounded-lg bg-[var(--surface-high)] px-4 py-2.5 text-xs text-[var(--on-surface-variant)]">
          {state.reason}
        </p>
      )}

      {state.kind === "unreachable" && (
        <div className="rounded-lg bg-[var(--warning-container)] text-[var(--on-warning-container)] px-4 py-2.5 text-xs flex items-start gap-2">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{state.reason}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-1.5 underline font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {state.kind === "ready" && state.apps.length === 0 && (
        <p className="text-xs text-[var(--on-surface-variant)] italic">
          No connectable apps are configured on this workspace yet.
        </p>
      )}

      <div
        className={
          layout === "grid"
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            : "space-y-2"
        }
      >
      {state.kind === "ready" &&
        state.apps.map((app) => {
          if (collapsing && !isFeatured(app.slug)) return null;
          const isConnecting = !app.connected && connecting.has(app.slug);
          const failure = failures[app.slug];
          return (
            <div
              key={app.slug}
              className={[
                "border bg-[var(--surface-container)] p-4 flex gap-3",
                layout === "grid"
                  ? "rounded-2xl flex-col items-start justify-between"
                  : "rounded-xl items-center justify-between gap-4",
                failure ? "border-[var(--error)]" : "border-[var(--outline-variant)]",
              ].join(" ")}
            >
              <div
                className={[
                  "min-w-0",
                  layout === "grid" ? "flex flex-col items-start gap-2 w-full" : "flex items-center gap-3",
                ].join(" ")}
              >
                <AppLogo src={app.logo} name={displayName(app)} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--on-surface)]">
                    {displayName(app)}
                    {isFeatured(app.slug) && (
                      <span className="ml-2 rounded-full bg-[var(--primary-muted)] px-2 py-0.5 text-[10px] font-medium text-[var(--primary)]">
                        Recommended
                      </span>
                    )}
                  </p>
                  {app.connected ? (
                    <p className="flex items-center gap-1 text-xs text-[var(--on-surface-variant)]">
                      <Check className="size-3" /> Connected
                    </p>
                  ) : isConnecting && blockedUrls[app.slug] ? (
                    <p className="text-xs text-[var(--on-surface-variant)]">
                      Your browser blocked the new tab.{" "}
                      <a
                        href={blockedUrls[app.slug]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-[var(--primary)] underline"
                      >
                        Open connection page
                      </a>
                    </p>
                  ) : isConnecting ? (
                    <p className="text-xs text-[var(--on-surface-variant)]">
                      Waiting for you to approve it in the other tab…
                    </p>
                  ) : app.tools_count === 0 ? (
                    /* Connecting this completes an OAuth flow and gives the
                       agent no tools to use. Offering it as a plain Connect
                       button would be a button that works and buys nothing. */
                    <p className="text-xs text-[var(--on-surface-variant)]">
                      No tools available yet — connecting this would not give your agents
                      anything to use.
                    </p>
                  ) : app.in_catalog === false ? (
                    /* Composio's catalog does not contain this slug, so there
                       is nothing to connect to. A fact, unlike the auth-scheme
                       guess this replaced — which wrongly blocked five apps
                       that worked. */
                    <p className="flex items-start gap-1 text-xs text-[var(--on-surface-variant)]">
                      <AlertTriangle className="size-3 shrink-0 mt-0.5" />
                      Not available from our connection provider.
                    </p>
                  ) : failure ? (
                    /* A failed attempt must not read as "never tried". */
                    <p className="flex items-start gap-1 text-xs text-[var(--error)]">
                      <AlertTriangle className="size-3 shrink-0 mt-0.5" /> {failure}
                    </p>
                  ) : (
                    <p className="text-xs text-[var(--on-surface-variant)]">Not connected</p>
                  )}
                </div>
              </div>

              {app.connected ? (
                <button
                  type="button"
                  disabled={busy === app.slug}
                  onClick={() => void handleDisconnect(app.slug)}
                  className={[
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)] transition-colors disabled:opacity-50",
                    layout === "grid" ? "w-full justify-center border border-[var(--outline-variant)]" : "shrink-0",
                  ].join(" ")}
                >
                  <Trash2 className="size-3.5" /> Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  disabled={busy === app.slug || app.tools_count === 0 || app.in_catalog === false}
                  onClick={() => void handleConnect(app.slug)}
                  className={[
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 disabled:opacity-50",
                    layout === "grid" ? "w-full justify-center" : "shrink-0",
                  ].join(" ")}
                >
                  {isConnecting ? "Reopen link" : failure ? "Try again" : "Connect"}
                  <ExternalLink className="size-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {collapsing && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs font-medium text-[var(--primary)] underline-offset-2 hover:underline"
        >
          Show all {state.kind === "ready" ? state.apps.length : ""} apps
        </button>
      )}
    </section>
  );
}

/** Featured slugs first (in the caller's order), everything else as the hub sent it. */
function featureFirst(apps: AppRow[], featuredKey: string): AppRow[] {
  if (!featuredKey) return apps;
  const order = featuredKey.split(",");
  const rank = (slug: string) => {
    const i = order.indexOf(slug.toLowerCase());
    return i === -1 ? order.length : i;
  };
  // Array.prototype.sort is stable, so non-featured apps keep the hub's order.
  return [...apps].sort((a, b) => rank(a.slug) - rank(b.slug));
}
